import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Download,
  Zap,
  Layers,
  Check,
  Copy,
  ChevronRight,
  Menu,
  X,
  Box,
  Cog,
  Grid3X3,
  Brackets,
  RotateCcw,
  Settings2,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Button } from "../components/ui/button";
import EmailCaptureModal from "../components/EmailCaptureModal";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPlanType, setModalPlanType] = useState("free");
  const [earlyAdopterAvailable, setEarlyAdopterAvailable] = useState(true);
  const [earlyAdopterRemaining, setEarlyAdopterRemaining] = useState(100);
  const [config, setConfig] = useState({
    download_link: "#",
    stripe_links: {
      early_adopter: "#",
      standard: "#",
    },
  });

  const getUserId = () => {
    let id = localStorage.getItem("ps_user_id");
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem("ps_user_id", id);
    }
    return id;
  };

  const trackEvent = useCallback(async (eventType, email = null, metadata = {}) => {
    const payload = {
      event: eventType,
      email,
      metadata,
      user_id: getUserId(),
      timestamp: new Date().toISOString(),
    };

    try {
      if (window.posthog && window.posthog.capture) {
        window.posthog.capture(eventType, payload);
      }

      await axios.post(`${API_URL}/api/events`, payload);
      console.log("Event tracked:", payload);
    } catch (error) {
      console.error("Failed to track event:", error);
    }
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/config`);
      setConfig(response.data);
      setEarlyAdopterAvailable(response.data.early_adopter_available);
      setEarlyAdopterRemaining(response.data.early_adopter_remaining);
    } catch (error) {
      console.error("Failed to fetch config:", error);
    }
  };

  useEffect(() => {
    trackEvent("page_visit");
    fetchConfig();
  }, [trackEvent]);

  const handleDownloadClick = (planType) => {
    trackEvent("download_clicked", null, { plan_type: planType });
    setModalPlanType(planType);
    setModalOpen(true);
  };

  const [upgradeLoading, setUpgradeLoading] = useState(false);

  const handleUpgradeClick = async () => {
    trackEvent("upgrade_clicked", null, { plan_type: "early_adopter" });
    
    // First, check if user has provided email (from modal or existing lead)
    const userEmail = localStorage.getItem("paramsnap_email");
    
    if (userEmail) {
      // Create checkout session directly
      await createCheckoutSession(userEmail, "early_adopter");
    } else {
      // Show email capture modal first
      setModalPlanType("early_adopter");
      setModalOpen(true);
    }
  };

  const handleStandardClick = async () => {
    trackEvent("upgrade_clicked", null, { plan_type: "standard" });
    
    const userEmail = localStorage.getItem("paramsnap_email");
    
    if (userEmail) {
      await createCheckoutSession(userEmail, "standard");
    } else {
      setModalPlanType("standard");
      setModalOpen(true);
    }
  };

  const createCheckoutSession = async (email, plan) => {
    setUpgradeLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/checkout/create-session`, {
        email: email,
        plan: plan,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Failed to create checkout session:", error);
      const errorMsg = error.response?.data?.detail || "Failed to start checkout";
      alert(errorMsg);
      setUpgradeLoading(false);
    }
  };

  // Export for modal to use
  const handleUpgradeAfterEmail = async (email, plan) => {
    // Store email for future use
    localStorage.setItem("paramsnap_email", email);
    await createCheckoutSession(email, plan);
  };

  const handleCopy = async () => {
    const installSteps = `1. Download zip file
2. Place in Fusion AddIns folder
3. Launch from Scripts and Add-Ins`;
    
    try {
      await navigator.clipboard.writeText(installSteps);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = installSteps;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        console.log('Copy failed');
      }
      document.body.removeChild(textArea);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="relative min-h-screen bg-[#11161C] overflow-hidden">
  
      {/* Metal texture */}
      <div
        className="absolute inset-0 bg-cover bg-center pointer-events-none"
        style={{
          backgroundImage: "url('/metal-texture.jpg')",
          opacity: 0.18,
        }}
      />
  
      {/* Cool blue steel glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at top center, rgba(59,130,246,0.10) 0%, rgba(59,130,246,0.03) 30%, transparent 60%)",
        }}
      />
  
      {/* Vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at center, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.15) 45%, rgba(0,0,0,0.55) 100%)",
        }}
      />
  
      {/* Blueprint grid */}
      <div
        className="absolute inset-0 opacity-[0.025] pointer-events-none
          bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),
               linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)]
          bg-[size:40px_40px]"
      />
  
      {/* Content */}
      <div className="relative z-10">
  
      {/* Email Capture Modal */}
      <EmailCaptureModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        planType={modalPlanType}
        onUpgradeAfterEmail={handleUpgradeAfterEmail}
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-[#0A0A0B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center gap-2 flex-shrink-0">
              <Box className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
              <span className="text-lg sm:text-xl font-bold tracking-tight" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                ParamSnap
              </span>
            </div>

            <div className="hidden md:flex items-center gap-6 lg:gap-8">
               <button 
                  onClick={() => scrollToSection('features')}
                  className="text-sm text-zinc-400 hover:text-white transition-colors"
                  data-testid="nav-features"
                >
                  Features
                </button>
             
              <button 
                onClick={() => scrollToSection('pricing')}
                className="text-sm text-zinc-400 hover:text-white transition-colors"
                data-testid="nav-pricing"
              >
                Pricing
              </button>
              <button 
                onClick={() => scrollToSection('faq')}
                className="text-sm text-zinc-400 hover:text-white transition-colors"
                data-testid="nav-faq"
              >
                FAQ
              </button>
              <Button 
                className="btn-primary px-4 py-2 rounded-md text-sm font-medium"
                data-testid="nav-download-btn"
                onClick={() => handleDownloadClick("free")}
              >
                Try It Free
              </Button>
            </div>

            <button 
              className="md:hidden p-2 -mr-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden py-4 border-t border-white/5"
            >
              <div className="flex flex-col gap-4">
                <button 
                  onClick={() => scrollToSection('features')}
                  className="text-sm text-zinc-400 hover:text-white transition-colors text-left py-2"
                >
                  Features
                </button>
                <button 
                  onClick={() => scrollToSection('pricing')}
                  className="text-sm text-zinc-400 hover:text-white transition-colors text-left py-2"
                >
                  Pricing
                </button>
                <button 
                  onClick={() => scrollToSection('faq')}
                  className="text-sm text-zinc-400 hover:text-white transition-colors text-left py-2"
                >
                  FAQ
                </button>
                <Button 
                  className="btn-primary px-4 py-3 rounded-md text-sm font-medium w-full"
                  data-testid="mobile-download-btn"
                  onClick={() => handleDownloadClick("free")}
                >
                  Try It Free
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </nav>

  {/* Hero Section */}
  <section id="hero" className="pt-24 pb-16 sm:pt-32 sm:pb-24 md:pt-40 md:pb-32 relative" data-testid="hero-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
        className="text-center max-w-4xl mx-auto"
      >
        <motion.p
          variants={fadeInUp}
          className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.15em] sm:tracking-[0.2em] text-blue-500 mb-4 sm:mb-6"
        >
          USED INSIDE FUSION 360
        </motion.p>
  
        <motion.h1
          variants={fadeInUp}
          className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tight sm:tracking-tighter leading-[1.15] sm:leading-[1.1] text-white mb-4 sm:mb-6 px-2"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          Stop rebuilding the same parts in Fusion 360
        </motion.h1>
  
        <motion.p
          variants={fadeInUp}
          className="text-sm sm:text-base md:text-lg text-zinc-400 leading-relaxed max-w-2xl mx-auto mb-6 sm:mb-8 px-2"
        >
          Generate plates, brackets, rolls, and assemblies in seconds — all fully editable inside Fusion.
        </motion.p>
  
        <motion.div
          variants={fadeInUp}
          className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 px-4 sm:px-0"
        >
          <Button
            className="btn-primary px-6 sm:px-8 py-3 rounded-md text-sm sm:text-base font-medium flex items-center justify-center gap-2 w-full sm:w-auto"
            data-testid="hero-download-btn"
            onClick={() => handleDownloadClick("free")}
          >
            <Download className="w-4 h-4 sm:w-5 sm:h-5" />
            Try It Free
          </Button>
  
          <Button
            variant="outline"
            className={`btn-secondary px-6 sm:px-8 py-3 rounded-md text-sm sm:text-base font-medium flex items-center justify-center gap-2 w-full sm:w-auto ${!earlyAdopterAvailable ? "opacity-60" : ""}`}
            data-testid="hero-early-adopter-btn"
            onClick={() => earlyAdopterAvailable && handleUpgradeClick()}
            disabled={!earlyAdopterAvailable}
          >
            {earlyAdopterAvailable ? (
              <>
                Get Unlimited Builds
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </>
            ) : (
              "Sold Out"
            )}
          </Button>
        </motion.div>
  
        {earlyAdopterAvailable && earlyAdopterRemaining <= 20 && (
          <motion.p
            variants={fadeInUp}
            className="text-xs sm:text-sm text-blue-400 mt-4"
          >
            Only {earlyAdopterRemaining} Early Adopter spots left!
          </motion.p>
        )}
      </motion.div>
  
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mt-12 sm:mt-16 md:mt-20 max-w-4xl mx-auto px-4 sm:px-0"
      >
        <div className="relative aspect-video rounded-md overflow-hidden border border-white/10 card-industrial">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
        >
          <source src="/demo.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/15 pointer-events-none" />
      </div>
  
        
        <div className="text-center text-xs text-zinc-600 mt-1">
          Built alongside real shop use cases
        </div>
      </motion.div>
    </div>
  </section>
      {/* Part Library Section */}
      <section id="features" className="py-16 sm:py-24 md:py-32 bg-[#0D0D0E]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      
          <p className="text-sm text-zinc-300 text-center mb-10">
            Built from repeat shop work — plates, bearing mounts, brackets, rolls
          </p>
      
          <div className="border border-white/10 bg-[#161A1D]">
            <div className="px-6 py-4 text-xs uppercase tracking-[0.2em] text-zinc-400 border-b border-white/10">
              Current Part Library
            </div>
      
            <div className="grid grid-cols-3 text-sm">
      
              <div className="p-4 text-zinc-400">Part Type</div>
              <div className="p-4 text-zinc-400">Editable Params</div>
              <div className="p-4 text-zinc-400">Typical Use</div>
      
              <div className="p-4 text-white border-t border-white/5">Bearing Plate</div>
              <div className="p-4 text-zinc-300 border-t border-white/5">
                Width, height, holes, spacing, thickness
              </div>
              <div className="p-4 text-zinc-300 border-t border-white/5">
                Mount bearings
              </div>
      
              <div className="p-4 text-white border-t border-white/5">Roll</div>
              <div className="p-4 text-zinc-300 border-t border-white/5">
                Diameter, length, journals
              </div>
              <div className="p-4 text-zinc-300 border-t border-white/5">
                Conveyor work
              </div>
      
              <div className="p-4 text-white border-t border-white/5">Gusset</div>
              <div className="p-4 text-zinc-300 border-t border-white/5">
                Leg 1, leg 2, thickness
              </div>
              <div className="p-4 text-zinc-300 border-t border-white/5">
                Reinforcement
              </div>
              <div className="p-4 text-white border-t border-white/5">L Bracket</div>
              <div className="p-4 text-zinc-300 border-t border-white/5">
                Arm length, wall length
              </div>
              <div className="p-4 text-zinc-300 border-t border-white/5">
                Mounts
              </div>
      
            </div>
          </div>
      
          <p className="text-center text-zinc-300 mt-16 text-lg font-medium">
            If you’ve rebuilt the same part more than twice, this pays for itself.
          </p>
      
        </div>
      </section>
      
      {/* Supported Parts Section */}
      <section className="py-16 sm:py-24 md:py-32 bg-[#0D0D0E]" data-testid="parts-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-10 sm:mb-16"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.15em] sm:tracking-[0.2em] text-blue-500 mb-3 sm:mb-4"
            >
              CURRENT PART TYPES
            </motion.p>
            <motion.h2 
              variants={fadeInUp}
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white"
              style={{ fontFamily: 'Inter Tight, sans-serif' }}
            >
              Parts you can generate right now
            </motion.h2>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4"
          >
            {[
              { name: "Two Bolt Bearing Plate", icon: Grid3X3 },
              { name: "Flange Bearing Bracket", icon: Brackets },
              { name: "Roll", icon: Cog },
              { name: "Gusset", icon: Box },
              { name: "L Bracket", icon: Brackets },
              { name: "Bolt Circle Pattern", icon: RotateCcw },
              { name: "More coming soon", icon: Settings2, muted: true }
            ].map((part, index) => (
              <motion.div 
                key={index}
                variants={fadeInUp}
                className={`card-industrial p-4 sm:p-6 text-center ${part.muted ? 'opacity-60' : ''}`}
              >
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-md ${part.muted ? 'bg-zinc-800' : 'bg-blue-500/10'} flex items-center justify-center mx-auto mb-3 sm:mb-4`}>
                  <part.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${part.muted ? 'text-zinc-500' : 'text-blue-500'}`} />
                </div>
                <p className={`text-xs sm:text-sm font-medium ${part.muted ? 'text-zinc-500' : 'text-white'}`}>
                  {part.name}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 sm:py-24 md:py-32" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-10 sm:mb-16"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.15em] sm:tracking-[0.2em] text-blue-500 mb-3 sm:mb-4"
            >
              WORKFLOW
            </motion.p>
            <motion.h2 
              variants={fadeInUp}
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white"
              style={{ fontFamily: 'Inter Tight, sans-serif' }}
            >
              How ParamSnap fits into your process
            </motion.h2>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 sm:grid-cols-2 gap-8"
          >
            {[
              { step: "01", title: "Download ParamSnap", desc: "Get the free add-in" },
              { step: "02", title: "Install in Fusion 360", desc: "Place in AddIns folder" },
              { step: "03", title: "Generate 3 parts free", desc: "Try before you commit" },
              { step: "04", title: "Unlock unlimited builds", desc: "Keep generating without limits" }
            ].map((item, index) => (
              <motion.div 
                key={index}
                variants={fadeInUp}
                className="relative"
              >
                <div className="text-4xl sm:text-5xl md:text-6xl font-mono font-bold text-blue-500/20 mb-2 sm:mb-4">
                  {item.step}
                </div>
                <h3 className="text-sm sm:text-lg md:text-xl font-semibold text-white mb-1 sm:mb-2" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                  {item.title}
                </h3>
                <p className="text-xs sm:text-sm text-zinc-400">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Install Instructions Section */}
      <section className="py-16 sm:py-24 md:py-32 bg-[#0D0D0E]" data-testid="install-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-8 sm:mb-12"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.15em] sm:tracking-[0.2em] text-blue-500 mb-3 sm:mb-4"
            >
              INSTALLATION
            </motion.p>
            <motion.h2 
              variants={fadeInUp}
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white"
              style={{ fontFamily: 'Inter Tight, sans-serif' }}
            >
              Setup in under 60 seconds
            </motion.h2>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-industrial"
          >
            <div className="terminal-header">
              <div className="terminal-dot red"></div>
              <div className="terminal-dot yellow"></div>
              <div className="terminal-dot green"></div>
              <span className="ml-3 sm:ml-4 text-[10px] sm:text-xs text-zinc-500 hidden sm:inline">Installation Steps</span>
              <button 
                onClick={handleCopy}
                className="ml-auto p-1 hover:bg-white/5 rounded transition-colors"
                data-testid="copy-install-btn"
              >
                {copied ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <Copy className="w-4 h-4 text-zinc-500" />
                )}
              </button>
            </div>
            <div className="p-4 sm:p-6 font-mono text-xs sm:text-sm">
              <div className="text-zinc-400">
                <span className="text-blue-400">1.</span> Download zip file
              </div>
              <div className="text-zinc-400 mt-2">
                <span className="text-blue-400">2.</span> Place in Fusion AddIns folder
              </div>
              <div className="text-zinc-400 mt-2">
                <span className="text-blue-400">3.</span> Launch from Scripts and Add-Ins
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What This Actually Fixes */}
<section className="py-16 sm:py-24 md:py-32 bg-[#0D0D0E]">
  <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
    <p className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.2em] text-blue-500 mb-4">
      WHAT THIS ACTUALLY FIXES
    </p>

    <h2
      className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4"
      style={{ fontFamily: 'Inter Tight, sans-serif' }}
    >
      Built for the parts you keep remaking
    </h2>

    <div className="text-center text-xs text-zinc-600 mb-8">
      Same part. Same result. Completely different time.
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
      <div className="p-6 border border-white/10 bg-[#161A1D]">
        <div className="text-[10px] uppercase tracking-[0.2em] text-blue-500 mb-3">
          Repeat Work
        </div>
        <h3 className="text-lg font-semibold text-white mb-2">
          Stop rebuilding the same geometry
        </h3>
        <p className="text-sm text-zinc-400">
          Plates, brackets, rolls, and common shop parts generated from templates instead of redrawing every time.
        </p>
      </div>

      <div className="p-6 border border-white/10 bg-[#161A1D]">
        <div className="text-[10px] uppercase tracking-[0.2em] text-blue-500 mb-3">
          Change Orders
        </div>
        <h3 className="text-lg font-semibold text-white mb-2">
          Adjust parts without starting over
        </h3>
        <p className="text-sm text-zinc-400">
          Change dimensions instantly—no need to rebuild sketches or features from scratch.
        </p>
      </div>

      <div className="p-6 border border-white/10 bg-[#161A1D]">
        <div className="text-[10px] uppercase tracking-[0.2em] text-blue-500 mb-3">
          Usage Standard
        </div>
        <h3 className="text-lg font-semibold text-white mb-2">
          Keep engineers using the same part standards
        </h3>
        <p className="text-sm text-zinc-400">
          Generate parts from consistent templates instead of one-off designs across the team.
        </p>
      </div>
    </div>
  </div>
</section>
      {/* Access Section */}
      <section id="pricing" className="py-16 sm:py-24 md:py-32" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-10 sm:mb-16"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.15em] sm:tracking-[0.2em] text-blue-500 mb-3 sm:mb-4"
            >
              STOP WASTING TIME
            </motion.p>
            <motion.h2 
              variants={fadeInUp}
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white"
              style={{ fontFamily: 'Inter Tight, sans-serif' }}
            >
              Pick how you want to stop rebuilding parts
            </motion.h2>
          </motion.div>

          <div className="text-center text-sm text-zinc-500 mb-6">
            One hour of rebuild time > one year of ParamSnap
          </div>
          <div className="text-center text-xs text-zinc-600 mb-6">
            If you build the same parts more than once — this pays for itself immediately.
          </div>
          
                    
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto"
          >
            {/* Free Tier */}
            <motion.div 
              variants={fadeInUp}
              className="card-industrial p-6 sm:p-8 flex flex-col relative"
            >
              <div className="mb-4 sm:mb-6">
                <h3 className="text-base sm:text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                  Free
                </h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl sm:text-4xl font-bold text-white">$0</span>
                </div>
                <p className="text-xs sm:text-sm text-zinc-500 mt-2">Test it on your real workflow</p>
              </div>
              <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8 flex-grow">
                <li className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-zinc-400">
                  <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  Generate 3 parts (real workflow test)
                </li>
                <li className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-zinc-400">
                  <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  Access to all templates
                </li>
                <li className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-zinc-400">
                  <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  Full parametric control
                </li>
              </ul>
              <Button 
                variant="outline"
                className="btn-secondary w-full py-2.5 sm:py-3 rounded-md text-xs sm:text-sm font-medium"
                data-testid="pricing-free-btn"
                onClick={() => handleDownloadClick("free")}
              >
                Try It Free
              </Button>
            </motion.div>

            {/* Early Adopter Tier */}
            <motion.div 
              variants={fadeInUp}
              className="card-industrial pricing-highlight p-6 sm:p-8 pt-10 sm:pt-12 flex flex-col relative"
            >
              <div className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-600 border border-white/10 rounded-md text-[10px] sm:text-xs font-medium text-white whitespace-nowrap z-10">
                Popular
              </div>
              <div className="mb-4 sm:mb-6 mt-2">
                <h3 className="text-base sm:text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                  Early Adopter
                </h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl sm:text-4xl font-bold text-white">$50</span>
                  <span className="text-xs sm:text-sm text-zinc-500">/year</span>
                </div>
                <p className="text-xs sm:text-sm text-blue-400 mt-2">
                  Costs less than one hour of rebuild time per year
                </p>
              </div>
              <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8 flex-grow">
                <li className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-zinc-400">
                  <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  Unlimited generations
                </li>
                <li className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-zinc-400">
                  <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  Priority access to new parts
                </li>
                <li className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-zinc-400">
                  <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  Locked-in pricing forever
                </li>
                <li className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-zinc-400">
                  <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  Email support
                </li>
              </ul>
              <p className="text-xs text-zinc-500 mt-2">
               → Stops you from rebuilding the same parts across projects
              </p>
              <Button 
                className={`w-full py-2.5 sm:py-3 rounded-md text-xs sm:text-sm font-medium ${earlyAdopterAvailable ? 'btn-primary' : 'bg-zinc-700 cursor-not-allowed'}`}
                data-testid="pricing-early-adopter-btn"
                onClick={() => earlyAdopterAvailable && handleUpgradeClick()}
                disabled={!earlyAdopterAvailable}
              >
                {earlyAdopterAvailable ? "Get Unlimited Builds" : "Sold Out"}
              </Button>
            </motion.div>

            {/* Standard Tier */}
            <motion.div 
              variants={fadeInUp}
              className="card-industrial p-6 sm:p-8 flex flex-col relative"
            >
              <div className="mb-4 sm:mb-6">
                <h3 className="text-base sm:text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                  Standard
                </h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl sm:text-4xl font-bold text-white">$100</span>
                  <span className="text-xs sm:text-sm text-zinc-500">/year</span>
                </div>
                <p className="text-xs sm:text-sm text-zinc-500 mt-2">For teams that don’t want limits</p>
              </div>
              <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8 flex-grow">
                <li className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-zinc-400">
                  <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  Unlimited generations
                </li>
                <li className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-zinc-400">
                  <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  All templates included
                </li>
                <li className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-zinc-400">
                  <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  Email support
                </li>
              </ul>
              <p className="text-xs text-zinc-500 mt-2">
                → Keeps every engineer using the same part standards
              </p>
              <Button 
                variant="outline"
                className="btn-secondary w-full py-2.5 sm:py-3 rounded-md text-xs sm:text-sm font-medium"
                data-testid="pricing-standard-btn"
                onClick={handleStandardClick}
              >
                Choose Standard
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 sm:py-24 md:py-32 bg-[#0D0D0E]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-10 sm:mb-16"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.15em] sm:tracking-[0.2em] text-blue-500 mb-3 sm:mb-4"
            >
              SUPPORT
            </motion.p>
            <motion.h2 
              variants={fadeInUp}
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white"
              style={{ fontFamily: 'Inter Tight, sans-serif' }}
            >
              Frequently Asked Questions
            </motion.h2>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Accordion type="single" collapsible className="space-y-3 sm:space-y-4">
              {[
                { id: "1", q: "What is ParamSnap?", a: "ParamSnap is a Fusion 360 add-in that allows you to instantly generate parametric engineering parts from prebuilt templates. Instead of modeling common parts from scratch, you select a template, adjust parameters, and generate a fully editable part." },
                { id: "2", q: "Does it work inside Fusion 360?", a: "Yes! ParamSnap runs directly inside Fusion 360 as an add-in. Once installed, you can access it from the Scripts and Add-Ins menu in the Design workspace." },
                { id: "3", q: "What happens after the 3 free builds?", a: "After using your 3 free generations, you'll need to upgrade to either the Early Adopter or Standard plan to continue generating parts. Your existing generated parts remain fully functional and editable." },
                { id: "4", q: "Are parts editable?", a: "Absolutely. All generated parts are fully parametric. You can modify any dimension, add features, or integrate them into larger assemblies just like any native Fusion 360 component." },
                { id: "5", q: "Is pricing annual?", a: "Yes, both the Early Adopter ($50) and Standard ($100) plans are billed annually. Early Adopter pricing is locked in for life as long as you maintain your subscription." }
              ].map((item) => (
                <AccordionItem key={item.id} value={`item-${item.id}`} className="card-industrial border-none">
                  <AccordionTrigger className="px-4 sm:px-6 py-3 sm:py-4 text-left text-sm sm:text-base text-white hover:no-underline" data-testid={`faq-item-${item.id}`}>
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="px-4 sm:px-6 pb-3 sm:pb-4 text-xs sm:text-sm text-zinc-400">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 sm:py-20 md:py-32 border-t border-white/5" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-12 mb-12 sm:mb-16">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Box className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500" />
                <span
                  className="text-xl sm:text-2xl font-bold tracking-tight"
                  style={{ fontFamily: 'Inter Tight, sans-serif' }}
                >
                  ParamSnap
                </span>
              </div>
      
              <p className="text-xs sm:text-sm text-zinc-400 max-w-sm mb-4 sm:mb-6">
                Generate common Fusion 360 parts from parametric templates and stop rebuilding the same geometry over and over.
              </p>
      
              <a
                href="mailto:support@paramsnap.com"
                className="text-blue-500 hover:text-blue-400 text-xs sm:text-sm font-medium transition-colors"
                data-testid="footer-support-email"
              >
                support@paramsnap.com
              </a>
            </div>
      
            <div>
              <h4
                className="text-sm font-semibold text-white mb-3 sm:mb-4"
                style={{ fontFamily: 'Inter Tight, sans-serif' }}
              >
                Product
              </h4>
      
              <ul className="space-y-2 sm:space-y-3">
                {["Features", "Pricing", "FAQ"].map((item) => (
                  <li key={item}>
                    <button
                      onClick={() => scrollToSection(item.toLowerCase())}
                      className="text-xs sm:text-sm text-zinc-400 hover:text-white transition-colors"
                    >
                      {item}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
      
          <div className="section-divider mb-6 sm:mb-8"></div>
      
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 text-center sm:text-left">
            <p className="text-[10px] sm:text-xs text-zinc-500">
              © {new Date().getFullYear()} ParamSnap. All rights reserved.
            </p>
      
            <p className="text-[10px] sm:text-xs text-zinc-500">
              Designed by <span className="text-zinc-400">Summit Automation</span> · Powered by{" "}
              <span className="text-zinc-400">Ka Rei On Technologies</span>
            </p>
                    </div>
                  </div>
                </footer>
              </div>
            </div>
              );
            };
            
            export default LandingPage;
